import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Car, CarWithLoadedModel } from '../api/carsApi';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import Select from '@/components/form/Select';
import Button from '@/components/ui/button/Button';
import { useUploadCarImage, useCarEnums } from '../hooks/useCars';
import { useToast } from '@/components/ui/toast/Toast';
import { useCarModels } from '@/features/car-models/hooks/useCarModels';
import Checkbox from '@/components/form/input/Checkbox';
import { CAR_COLORS } from '@/shared/constants/colors';
import TextArea from '@/components/form/input/TextArea';
import { withFormProtection } from '@/components/form/withFormProtection';
import ImagePreview from '@/components/ui/images/ImagePreview';
import { useTranslation } from 'react-i18next';

interface CarEditFormProps {
  car: CarWithLoadedModel;
  onSubmit: (data: Partial<Car>) => Promise<void>;
  onClose: () => void;
  initialData: CarWithLoadedModel;
  currentData?: FormData;
  onDataChange?: (data: FormData) => void;
}

interface FormData {
  color: string;
  registration_number: string;
  release_year: number;
  description_en: string;
  description_fr: string;
  price: string;
  ecology_class: string;
  fuel_type: string;
  mileage_limit: number;
  drivetrain: string;
  fuel_consumption: string;
  engine_displacement: string;
  engine_power: string;
  seats_count: number;
  doors_count: number;
  has_gps: boolean;
  clearance: number;
  permitted_area_latitude: string;
  permitted_area_longitude: string;
  permitted_area_radius: number;
  slug: string;
  model: string;
}

interface FormErrors {
  registration_number?: string;
  color?: string;
  price?: string;
  slug?: string;
  model?: string;
}

const ColorOption = ({ value, label }: { value: string; label: string }) => (
  <div className="flex items-center gap-2">
    <div
      className="w-3 h-3 rounded-full"
      style={{ backgroundColor: value }}
    />
    {label}
  </div>
);

const CarEditForm = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean }, CarEditFormProps>(({ car, onSubmit, onClose, initialData, onDataChange }, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [showOriginalImage, setShowOriginalImage] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const { data: carModelsResponse } = useCarModels();
  const carModels = carModelsResponse?.data || [];
  const [formData, setFormData] = useState<FormData>({
    color: car.color || '',
    registration_number: car.registration_number || '',
    release_year: car.release_year,
    description_en: car.description_en || '',
    description_fr: car.description_fr || '',
    price: car.price || '',
    ecology_class: car.ecology_class || '',
    fuel_type: car.fuel_type || '',
    mileage_limit: car.mileage_limit || 0,
    drivetrain: car.drivetrain || '',
    fuel_consumption: car.fuel_consumption || '',
    engine_displacement: car.engine_displacement || '',
    engine_power: car.engine_power || '',
    seats_count: car.seats_count || 0,
    doors_count: car.doors_count || 0,
    has_gps: car.has_gps || false,
    clearance: car.clearance || 0,
    permitted_area_latitude: car.permitted_area_latitude || '',
    permitted_area_longitude: car.permitted_area_longitude || '',
    permitted_area_radius: car.permitted_area_radius || 0,
    slug: car.slug || '',
    model: car.model?.id || '',
  });

  useEffect(() => {
    if (onDataChange) {
      onDataChange(formData);
    }
  }, [formData, onDataChange]);

  const uploadImage = useUploadCarImage();
  const { data: enums } = useCarEnums();

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedImage(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset the file input value
      setShowOriginalImage(false);
    }
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, price: value }));
    if (value && !isNaN(Number(value))) {
      clearFieldError('price');
    }
  };

  const handleModelChange = (value: string) => {
    setFormData(prev => ({ ...prev, model: value }));
    if (value) {
      clearFieldError('model');
    }
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, slug: value }));
    if (value.trim()) {
      clearFieldError('slug');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.registration_number.trim()) {
      newErrors.registration_number = 'Registration number is required';
    }
    if (!formData.color) {
      newErrors.color = 'Color is required';
    }
    if (!formData.price || isNaN(Number(formData.price))) {
      newErrors.price = 'Valid price is required';
    }
    if (!formData.model) {
      newErrors.model = 'Car model is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearFieldError = (field: keyof FormErrors) => {
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleRegistrationNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, registration_number: value }));
    if (value.trim()) {
      clearFieldError('registration_number');
    }
  };

  const handleColorChange = (value: string) => {
    setFormData(prev => ({ ...prev, color: value }));
    if (value) {
      clearFieldError('color');
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit({
        ...formData,
        release_year: Number(formData.release_year)
      });

      if (selectedImage) {
        await uploadImage.mutateAsync({
          id: car.id,
          file: selectedImage
        });
      }

      toast.success(t('cars.edit.success'));
    } catch (error) {
      toast.error(t('cars.edit.error'));
    }
  };

    let imageUrl = undefined;

    if (showOriginalImage && car.image_url) {
      imageUrl = car.image_url;
    }

    if (selectedImage) {
      imageUrl = URL.createObjectURL(selectedImage);
    }

  return (
    <>
      <div className="px-6 py-4">
        <h5 className="text-xl font-semibold text-text-primary lg:text-2xl">
          {t('cars.edit.title')}
        </h5>
        <p className="text-sm text-text-tertiary">
          {t('cars.edit.subtitle')}
        </p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-[-8px] mb-[-18px]">
          {/* Basic Information */}
          <div className="col-span-1">
            <h3 className="text-lg font-medium mb-4 text-text-primary">{t('cars.create.basic_info')}</h3>

            <div className="grid grid-cols-[auto,1fr] gap-4">
              <div className="col-span-2 md:col-span-1">
                <Label>{t('cars.create.fields.car_image')}</Label>
                <div className="mt-2 flex items-center gap-4">
                  <div
                    className="relative w-32 h-32 border-[1px] border-border-secondary rounded-lg overflow-hidden cursor-pointer"
                    onClick={handleImageClick}
                  >
                    <div className="w-full h-full">
                      {imageUrl !== undefined ? (
                        <div
                          className="w-full h-full flex"
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <ImagePreview imageUrl={imageUrl}>
                            <img
                              src={imageUrl}
                              alt="Car preview"
                              className="w-full h-full object-cover"
                            />
                          </ImagePreview>
                        </div>
                      ) : (
                        <div className="relative w-32 h-32">
                          <div className="absolute inset-0 flex items-center justify-center bg-bg-tertiary bg-opacity-50 text-text-muted">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                          </div>
                        </div>
                      )}
                      {(imageUrl !== undefined) && (
                        <button
                          onClick={handleRemoveImage}
                          className="absolute top-2 right-2 z-10 bg-bg-secondary rounded-full p-1 shadow-md hover:bg-bg-hover"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-text-muted"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </div>
              </div>

              <div className="col-span-2 md:col-span-1">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 md:col-span-1">
                    <Label>{t('cars.create.fields.car_model')} *</Label>
                    <Select
                      testId="car-form-field-model"
                      defaultValue={formData.model}
                      onChange={handleModelChange}
                      options={carModels.map(model => ({
                        label: `${model.brand_name} ${model.model_name}`,
                        value: model.id
                      }))}
                      placeholder={t('cars.create.placeholders.select_model')}
                      error={errors.model}
                      disabled={true}
                    />
                  </div>

                  <div className="col-span-2 md:col-span-1">
                    <Label>{t('cars.create.fields.reg_number')} *</Label>
                    <Input
                      testId="car-form-field-registration-number"
                      type="text"
                      value={formData.registration_number}
                      onChange={handleRegistrationNumberChange}
                      placeholder={t('cars.create.placeholders.enter_reg_number')}
                      error={errors.registration_number}
                    />
                  </div>
                </div>

                <div className="col-span-2 md:col-span-1 mt-4">
                  <Label>{t('cars.create.fields.slug')} *</Label>
                  <Input
                    testId="car-form-field-slug"
                    type="text"
                    value={formData.slug}
                    onChange={handleSlugChange}
                    placeholder={t('cars.create.placeholders.enter_slug')}
                    error={errors.slug}
                  />
                </div>

              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="col-span-3 md:col-span-1">
                <Label>{t('cars.create.fields.color')} *</Label>
                <Select
                  testId="car-form-field-color"
                  options={CAR_COLORS}
                  onChange={handleColorChange}
                  defaultValue={formData.color}
                  placeholder={t('cars.create.placeholders.select_color')}
                  error={errors.color}
                  iconTemplate={(value) => (
                    <div
                      className="w-4 h-4 rounded-full border border-gray-400 mr-2"
                      style={{ backgroundColor: value }}
                    />
                  )}
                />
              </div>

              <div className="col-span-3 md:col-span-1">
                <Label>{t('cars.create.fields.release_year')}</Label>
                <Input
                  testId="car-form-field-release-year"
                  type="number"
                  value={formData.release_year}
                  onChange={(e) => setFormData(prev => ({ ...prev, release_year: parseInt(e.target.value) }))}
                  placeholder={t('cars.create.placeholders.enter_year')}
                />
              </div>

              <div className="col-span-3 md:col-span-1">
                <Label>{t('cars.create.fields.price')} *</Label>
                <Input
                  testId="car-form-field-price"
                  type="number"
                  value={formData.price}
                  onChange={handlePriceChange}
                  placeholder={t('cars.create.placeholders.enter_price')}
                  error={errors.price}
                />
              </div>
            </div>
          </div>

          {/* Technical Specifications */}
          <div className="col-span-1">
            <h3 className="text-lg font-medium mb-4 text-text-primary">{t('cars.create.technical_specs')}</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t('cars.create.fields.fuel_type')}</Label>
                <Select
                  testId="car-form-field-fuel_type"
                  defaultValue={formData.fuel_type}
                  onChange={(value: string) =>
                    setFormData(prev => ({ ...prev, fuel_type: value }))
                  }
                  options={enums?.fuel_type || []}
                  placeholder={t('cars.create.placeholders.select_fuel_type')}
                />
              </div>

              <div>
                <Label>{t('cars.create.fields.drivetrain')}</Label>
                <Select
                  testId="car-form-field-drivetrain"
                  defaultValue={formData.drivetrain}
                  onChange={(value: string) =>
                    setFormData(prev => ({ ...prev, drivetrain: value }))
                  }
                  options={enums?.drivetrain || []}
                  placeholder={t('cars.create.placeholders.select_drivetrain')}
                />
              </div>

              <div>
                <Label>{t('cars.create.fields.engine_displacement')}</Label>
                <Input
                  testId="car-form-field-engine_displacement"
                  type="text"
                  value={formData.engine_displacement}
                  onChange={(e) => setFormData(prev => ({ ...prev, engine_displacement: e.target.value }))}
                  placeholder={t('cars.create.placeholders.enter_engine')}
                />
              </div>

              <div>
                <Label>{t('cars.create.fields.engine_power')}</Label>
                <Input
                  testId="car-form-field-engine_power"
                  type="text"
                  value={formData.engine_power}
                  onChange={(e) => setFormData(prev => ({ ...prev, engine_power: e.target.value }))}
                  placeholder={t('cars.create.placeholders.enter_power')}
                />
              </div>

              <div>
                <Label>{t('cars.create.fields.fuel_consumption')}</Label>
                <Input
                  testId="car-form-field-fuel_consumption"
                  type="text"
                  value={formData.fuel_consumption}
                  onChange={(e) => setFormData(prev => ({ ...prev, fuel_consumption: e.target.value }))}
                  placeholder={t('cars.create.placeholders.enter_consumption')}
                />
              </div>

              <div>
                <Label>{t('cars.create.fields.ecology_class')}</Label>
                <Input
                  testId="car-form-field-ecology_class"
                  type="text"
                  value={formData.ecology_class}
                  onChange={(e) => setFormData(prev => ({ ...prev, ecology_class: e.target.value }))}
                  placeholder={t('cars.create.placeholders.enter_ecology')}
                />
              </div>
            </div>
          </div>

          {/* Descriptions */}
          <div className="col-span-1 lg:row-span-3">
            <h3 className="text-lg font-medium mb-4 text-text-primary">{t('cars.create.descriptions')}</h3>

            <div className="space-y-4">
              <div>
                <Label>{t('cars.create.fields.english')}</Label>
                <TextArea
                  testId="car-form-field-description-en"
                  value={formData.description_en}
                  onChange={(value) => setFormData(prev => ({ ...prev, description_en: value }))}
                  placeholder={t('cars.create.placeholders.enter_description_en')}
                  rows={5}
                />
              </div>

              <div>
                <Label>{t('cars.create.fields.french')}</Label>
                <TextArea
                  testId="car-form-field-description-fr"
                  value={formData.description_fr}
                  onChange={(value) => setFormData(prev => ({ ...prev, description_fr: value }))}
                  placeholder={t('cars.create.placeholders.enter_description_fr')}
                  rows={5}
                />
              </div>

              <div className="relative top-[5px]">
                <Checkbox
                  testId="car-form-field-has-gps"
                  label={t('cars.create.fields.gps')}
                  checked={formData.has_gps}
                  onChange={(checked: boolean) => setFormData(prev => ({ ...prev, has_gps: checked }))}
                />
              </div>
            </div>
          </div>

          {/* Permitted Area */}
          <div className="col-span-1">
            <h3 className="text-lg font-medium mb-4 text-text-primary">{t('cars.create.permitted_area')}</h3>

            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-3 md:col-span-1">
                <Label>{t('cars.create.fields.latitude')}</Label>
                <Input
                  testId="car-form-field-latitude"
                  type="text"
                  value={formData.permitted_area_latitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, permitted_area_latitude: e.target.value }))}
                  placeholder={t('cars.create.placeholders.enter_latitude')}
                />
              </div>
              <div className="col-span-3 md:col-span-1">
                <Label>{t('cars.create.fields.longitude')}</Label>
                <Input
                  testId="car-form-field-longitude"
                  type="text"
                  value={formData.permitted_area_longitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, permitted_area_longitude: e.target.value }))}
                  placeholder={t('cars.create.placeholders.enter_longitude')}
                />
              </div>
              <div className="col-span-3 md:col-span-1">
                <Label>{t('cars.create.fields.radius')}</Label>
                <Input
                  testId="car-form-field-radius"
                  type="number"
                  value={formData.permitted_area_radius}
                  onChange={(e) => setFormData(prev => ({ ...prev, permitted_area_radius: parseInt(e.target.value) }))}
                  placeholder={t('cars.create.placeholders.enter_radius')}
                />
              </div>
            </div>
          </div>

          {/* Interior & Features */}
          <div className="col-span-1 mb-4 md:mb-0">
            <h3 className="text-lg font-medium mb-4 text-text-primary">{t('cars.create.interior_features')}</h3>

            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-4 md:col-span-1">
                <Label>{t('cars.create.fields.seats')}</Label>
                <Input
                  testId="car-form-field-seats"
                  type="number"
                  value={formData.seats_count}
                  onChange={(e) => setFormData(prev => ({ ...prev, seats_count: parseInt(e.target.value) }))}
                  placeholder={t('cars.create.placeholders.enter_seats')}
                />
              </div>

              <div className="col-span-4 md:col-span-1">
                <Label>{t('cars.create.fields.doors')}</Label>
                <Input
                  testId="car-form-field-doors"
                  type="number"
                  value={formData.doors_count}
                  onChange={(e) => setFormData(prev => ({ ...prev, doors_count: parseInt(e.target.value) }))}
                  placeholder={t('cars.create.placeholders.enter_doors')}
                />
              </div>

              <div className="col-span-4 md:col-span-1">
                <Label>{t('cars.create.fields.clearance')}</Label>
                <Input
                  testId="car-form-field-clearance"
                  type="number"
                  value={formData.clearance}
                  onChange={(e) => setFormData(prev => ({ ...prev, clearance: parseInt(e.target.value) }))}
                  placeholder={t('cars.create.placeholders.enter_clearance')}
                />
              </div>

              <div className="col-span-4 md:col-span-1">
                <Label>{t('cars.create.fields.mileage_limit')}</Label>
                <Input
                  testId="car-form-field-mileage-limit"
                  type="number"
                  value={formData.mileage_limit}
                  onChange={(e) => setFormData(prev => ({ ...prev, mileage_limit: parseInt(e.target.value) }))}
                  placeholder={t('cars.create.placeholders.enter_mileage')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="flex items-center gap-3 sm:justify-end">
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('cars.create.buttons.cancel')}
          </Button>
          <Button
            testId="car-form-submit-button"
            variant="primary"
            onClick={handleSubmit}
            disabled={Object.keys(errors).length > 0}
          >
            {t('cars.edit.buttons.update')}
          </Button>
        </div>
      </div>
    </>
  );
});

CarEditForm.displayName = 'CarEditForm';

const ProtectedCarEditForm = withFormProtection(CarEditForm);

interface CarEditFormWrapperProps extends Omit<CarEditFormProps, 'currentData' | 'onDataChange'> {
  ref?: React.Ref<{ handleClose: () => void; isDirty: () => boolean }>;
}

const CarEditFormWrapper = forwardRef<{ handleClose: () => void; isDirty: () => boolean }, CarEditFormWrapperProps>((props, ref) => {
  const initialFormData = {
    color: props.car.color || '',
    registration_number: props.car.registration_number || '',
    release_year: props.car.release_year,
    description_en: props.car.description_en || '',
    description_fr: props.car.description_fr || '',
    price: props.car.price || '',
    ecology_class: props.car.ecology_class || '',
    fuel_type: props.car.fuel_type || '',
    mileage_limit: props.car.mileage_limit || 0,
    drivetrain: props.car.drivetrain || '',
    fuel_consumption: props.car.fuel_consumption || '',
    engine_displacement: props.car.engine_displacement || '',
    engine_power: props.car.engine_power || '',
    seats_count: props.car.seats_count || 0,
    doors_count: props.car.doors_count || 0,
    has_gps: props.car.has_gps || false,
    clearance: props.car.clearance || 0,
    permitted_area_latitude: props.car.permitted_area_latitude || '',
    permitted_area_longitude: props.car.permitted_area_longitude || '',
    permitted_area_radius: props.car.permitted_area_radius || 0,
    slug: props.car.slug || '',
    model: props.car.model?.id || '',
  };

  const [currentData, setCurrentData] = useState<FormData>(initialFormData);

  const handleDataChange = useCallback((data: FormData) => {
    setCurrentData(data);
  }, []);

  const { car, onClose, ...restProps } = props;

  return (
    <ProtectedCarEditForm
      {...restProps}
      ref={ref}
      car={car}
      onClose={onClose}
      initialData={initialFormData}
      currentData={currentData}
      onDataChange={handleDataChange}
    />
  );
});

CarEditFormWrapper.displayName = 'CarEditFormWrapper';

export default CarEditFormWrapper;
